/**
 * CurrentRecords.jsx
 *
 * Displays hill records for a single class (Open / Vintage / Classic /
 * Forgotten Era / Road Legal), as of a chosen date.
 *
 * URL:  /records?Class=vintage&Date=2016-10-03
 *
 * Subclasses arrive as [{key, label}]. Records are keyed by sc.key.
 * The HGV band normalises 1000/1300/1400 server-side; db_class on each
 * record carries the original DB value for display as a subtitle.
 */

import { useState, useEffect, useCallback } from 'react';

// ── Constants ──────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const API_KEY  = import.meta.env.VITE_API_KEY  ?? '';

const CLASS_OPTIONS = [
  { value: 'open',          label: 'Open',          short: 'Open'     },
  { value: 'vintage',       label: 'Vintage',       short: 'Vintage'  },
  { value: 'classic',       label: 'Classic',       short: 'Classic'  },
  { value: 'forgotten-era', label: 'Forgotten Era', short: 'FE'       },
  { value: 'road-legal',    label: 'Road Legal',    short: 'RL'       },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtTime(t) {
  if (t == null) return '–';
  return Number(t).toFixed(2);
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDateNumeric(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// Legacy DB rows use "?" as a literal placeholder for an unrecorded
// manufacturer — treat that the same as missing and omit it entirely.
function fmtManufacturer(m) {
  if (!m) return null;
  const trimmed = m.trim();
  return trimmed && trimmed !== '?' ? trimmed : null;
}

// ── HillCard ───────────────────────────────────────────────────────────────

function HillCard({ hill, subclasses, hillRecords, outright }) {
  const hasAny = subclasses.some(sc => hillRecords?.[sc.key]);
  const outrightManufacturer = fmtManufacturer(outright?.manufacturer);

  return (
    <div className="recs-hill-card">
      <div className="recs-hill-header">
        <span className="recs-hill-name">{hill}</span>
        {outright && (
          <span className="recs-hill-outright">
            Outright {fmtTime(outright.record)} — {outright.firstname} {outright.lastname}
            {outrightManufacturer && ` (${outrightManufacturer})`} · {fmtDate(outright.record_date)}
          </span>
        )}
      </div>

      {hasAny ? (
        <div className="recs-table-scroll">
          <table className="recs-table">
            <colgroup>
              <col className="recs-col-class" />
              <col className="recs-col-time" />
              <col className="recs-col-holder" />
              <col className="recs-col-machine" />
              <col className="recs-col-date" />
            </colgroup>
            <thead>
              <tr>
                <th className="recs-th recs-th-class">Class</th>
                <th className="recs-th recs-th-time">Record</th>
                <th className="recs-th recs-th-holder">Holder</th>
                <th className="recs-th recs-th-machine">Machine</th>
                <th className="recs-th recs-th-date">Set</th>
              </tr>
            </thead>
            <tbody>
              {subclasses.map(sc => {
                const rec = hillRecords?.[sc.key];
                const manufacturer = fmtManufacturer(rec?.manufacturer);
                return (
                  <tr key={sc.key} className={rec ? 'recs-row recs-row-set' : 'recs-row recs-row-empty'}>
                    <td className="recs-td recs-td-class">
                      {sc.label}
                      {/* Show original capacity for HGV rows, e.g. "(1300)" */}
                      {rec?.db_class && rec.db_class !== sc.key && (
                        <span className="recs-db-class"> ({rec.db_class})</span>
                      )}
                    </td>
                    {rec ? (
                      <>
                        <td className="recs-td recs-td-time">{fmtTime(rec.record)}</td>
                        <td className="recs-td recs-td-holder">
                          {rec.firstname} {rec.lastname}
                          {rec.passenger_firstname && (
                            <span className="recs-passenger"> / {rec.passenger_firstname} {rec.passenger_lastname}</span>
                          )}
                        </td>
                        <td className="recs-td recs-td-machine">{manufacturer ?? '–'}</td>
                        <td className="recs-td recs-td-date">{fmtDateNumeric(rec.record_date)}</td>
                      </>
                    ) : (
                      <td className="recs-td recs-td-empty" colSpan={4}>No record set</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="recs-no-records">No records set for this class at this hill.</p>
      )}
    </div>
  );
}

// ── Leaderboard ────────────────────────────────────────────────────────────

function Leaderboard({ entries }) {
  if (!entries || entries.length === 0) return null;

  return (
    <div className="recs-leaderboard-card">
      <div className="recs-leaderboard-header">
        <span className="recs-leaderboard-title">Most Records Held</span>
      </div>
      <table className="recs-leaderboard-table">
        <thead>
          <tr>
            <th className="recs-lb-th recs-lb-th-rank">#</th>
            <th className="recs-lb-th recs-lb-th-name">Rider</th>
            <th className="recs-lb-th recs-lb-th-count">Records</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((row, i) => (
            <tr key={`${row.firstname}-${row.lastname}-${i}`} className="recs-lb-row">
              <td className="recs-lb-td recs-lb-td-rank">{i + 1}</td>
              <td className="recs-lb-td recs-lb-td-name">{row.firstname} {row.lastname}</td>
              <td className="recs-lb-td recs-lb-td-count">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function CurrentRecords() {
  const params    = new URLSearchParams(window.location.search);
  const initClass = params.get('Class')?.toLowerCase() ?? 'open';
  const initDate  = params.get('Date') ?? todayISO();

  const [classKey, setClassKey] = useState(
    CLASS_OPTIONS.some(o => o.value === initClass) ? initClass : 'open'
  );
  const [date,    setDate]    = useState(initDate);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetchRecords = useCallback(async (cls, dt) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const url = `${API_BASE}/api/current-records?Class=${encodeURIComponent(cls)}&Date=${encodeURIComponent(dt)}`;
      const res = await fetch(url, { headers: { 'X-API-Key': API_KEY } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.error ?? `HTTP ${res.status}`);
        return;
      }
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch if Class is in the URL — Date defaults to today if omitted
  useEffect(() => {
    if (params.get('Class')) {
      fetchRecords(initClass, initDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hills      = data ? Object.keys(data.records) : [];
  const subclasses = data?.subclasses ?? [];  // [{key, label}]

  return (
    <div className="recs-page">

      {/* ── Controls ── */}
      <div className="recs-controls query-step">
        <h2>Current Records</h2>
        <p className="step-description">
          Select a class and a date to see which records were standing at that point.
        </p>

        <div className="recs-control-row">
          <div className="recs-control-group">
            <span className="recs-label">Class</span>
            <div className="recs-select-wrap">
              {CLASS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`recs-class-btn${classKey === opt.value ? ' active' : ''}`}
                  onClick={() => { setClassKey(opt.value); if (date) fetchRecords(opt.value, date); }}
                  type="button"
                >
                  <span className="recs-class-label-full">{opt.label}</span>
                  <span className="recs-class-label-short">{opt.short}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="recs-control-group">
            <label htmlFor="recs-date" className="recs-label">As of date</label>
            <input
              id="recs-date"
              type="date"
              className="recs-date-input"
              value={date}
              onChange={e => setDate(e.target.value)}
              max={todayISO()}
            />
          </div>

          <button
            className="reset-button"
            onClick={() => fetchRecords(classKey, date)}
            disabled={!date || loading}
            type="button"
            style={{ marginTop: 0, alignSelf: 'flex-end' }}
          >
            {loading ? 'Loading…' : 'Show Records'}
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="error-message">
          <h3>No results</h3>
          <p>{error}</p>
        </div>
      )}

      {/* ── Results ── */}
      {data && !loading && (
        <div className="recs-results">
          <div className="recs-summary-banner">
            <span className="recs-summary-class">{data.class_name}</span>
            <span className="recs-summary-sep">records as of</span>
            <span className="recs-summary-date">{fmtDate(data.as_of)}</span>
            <span className="recs-summary-count">
              {hills.length} hill{hills.length !== 1 ? 's' : ''}
            </span>
          </div>

          <Leaderboard entries={data.leaderboard} />

          {hills.length === 0 ? (
            <div className="empty-state">
              <p>No records found for {data.class_name} as of {fmtDate(data.as_of)}.</p>
            </div>
          ) : (
            <div className="recs-grid">
              {hills.map(hill => (
                <HillCard
                  key={hill}
                  hill={hill}
                  subclasses={subclasses}
                  hillRecords={data.records[hill]}
                  outright={data.outright?.[hill]}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
